import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ArrowLeft, AlertTriangle, ArrowDownCircle, ArrowUpCircle, Paperclip, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Badge, Button } from "@/components/ui-kit";
import { toast } from "sonner";

const fmtIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Math.round(n || 0));
const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Iter v34d (#9) — Kasbon portal khusus karyawan.
 * Menampilkan ringkasan kasbon bulan ini (reset tgl 1), sisa hutang total,
 * dan 10 transaksi terakhir.
 */
export default function KaryawanKasbon() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/employee-debts/me/summary");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat Kasbon");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6 lg:p-8" data-testid="kasbon-loading">
        <PageHeader overline="Karyawan" title="Kasbon Saya" description="Memuat..." />
      </div>
    );
  }

  if (!data?.allowed) {
    return (
      <div className="max-w-3xl mx-auto p-4 md:p-6 lg:p-8 space-y-4" data-testid="kasbon-not-allowed">
        <PageHeader overline="Karyawan" title="Kasbon Saya" />
        <Link to="/karyawan/absen" className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900">
          <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke Absensi
        </Link>
        <Card className="p-6 bg-amber-50 border-amber-300">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-700 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900">Kasbon belum diaktifkan</h4>
              <p className="text-sm text-amber-800 mt-1">{data?.message || "Anda belum memiliki izin Kasbon. Hubungi HR/Finance untuk diaktifkan."}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const balance = data.balance || 0;
  const monthDebt = data.month_debt || 0;
  const monthPayment = data.month_payment || 0;

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6 lg:p-8 space-y-6" data-testid="kasbon-page">
      <div className="flex items-center justify-between">
        <PageHeader overline={`Karyawan · ${data.employee_name || ""}`} title="Kasbon Saya"
          description={`Ringkasan kasbon bulan ${data.month_label}. Akumulasi bulanan reset setiap tanggal 1.`} />
        <Button variant="outline" onClick={load} data-testid="kasbon-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>

      <Link to="/karyawan/absen" className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke Absensi
      </Link>

      {/* Iter v34d — Three summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="kasbon-summary">
        <Card className={`p-5 border-l-4 ${balance > 0 ? "border-red-500" : balance < 0 ? "border-emerald-500" : "border-stone-300"}`}>
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Sisa Hutang</div>
          <div className={`text-2xl font-display font-bold ${balance > 0 ? "text-red-700" : balance < 0 ? "text-emerald-700" : "text-stone-700"}`} data-testid="kasbon-balance">
            {fmtIDR(Math.abs(balance))}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">
            {balance > 0 ? "Yang harus dilunasi" : balance < 0 ? "Saldo lebih (credit)" : "Lunas"}
          </div>
        </Card>

        <Card className="p-5 border-l-4 border-amber-400">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 flex items-center gap-1.5">
            <ArrowDownCircle className="h-3 w-3 text-amber-600" />Pinjam Bulan Ini
          </div>
          <div className="text-2xl font-display font-bold text-amber-700" data-testid="kasbon-month-debt">{fmtIDR(monthDebt)}</div>
          <div className="text-[11px] text-stone-500 mt-1">{data.month_label}</div>
        </Card>

        <Card className="p-5 border-l-4 border-emerald-400">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 flex items-center gap-1.5">
            <ArrowUpCircle className="h-3 w-3 text-emerald-600" />Bayar Bulan Ini
          </div>
          <div className="text-2xl font-display font-bold text-emerald-700" data-testid="kasbon-month-payment">{fmtIDR(monthPayment)}</div>
          <div className="text-[11px] text-stone-500 mt-1">{data.month_label}</div>
        </Card>
      </div>

      {/* Recent transactions */}
      <Card className="p-0 overflow-x-auto" data-testid="kasbon-recent">
        <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
          <Wallet className="h-4 w-4 text-stone-500" />
          <h3 className="font-display font-semibold text-stone-900 text-sm">Riwayat Terakhir (10 transaksi)</h3>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-600">
            <tr>
              <th className="text-left px-3 py-2.5">Tanggal</th>
              <th className="text-left px-3 py-2.5">Tipe</th>
              <th className="text-right px-3 py-2.5">Jumlah</th>
              <th className="text-left px-3 py-2.5">Catatan</th>
              <th className="text-center px-3 py-2.5">Bukti</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {data.recent.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-12 text-center text-xs text-stone-400">Belum ada transaksi kasbon.</td></tr>
            )}
            {data.recent.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`kasbon-row-${r.id}`}>
                <td className="px-3 py-2.5 font-mono text-[11px] text-stone-700">{fmtDT(r.ts)}</td>
                <td className="px-3 py-2.5">
                  {r.type === "debt"
                    ? <Badge tone="amber"><ArrowDownCircle className="h-3 w-3 inline mr-1" />Pinjam</Badge>
                    : <Badge tone="emerald"><ArrowUpCircle className="h-3 w-3 inline mr-1" />Bayar</Badge>}
                </td>
                <td className={`px-3 py-2.5 text-right font-mono ${r.type === "debt" ? "text-red-700" : "text-emerald-700"}`}>
                  {r.type === "debt" ? "+" : "−"}{fmtIDR(r.amount)}
                </td>
                <td className="px-3 py-2.5 text-stone-700 max-w-[260px] truncate" title={r.note}>{r.note || <span className="text-stone-400 italic">tanpa catatan</span>}</td>
                <td className="px-3 py-2.5 text-center">
                  {r.has_attachment ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/employee-debts/${r.id}/attachment`}
                      target="_blank" rel="noreferrer"
                      className="text-terra-700 hover:text-terra-900 underline decoration-dotted inline-flex items-center gap-1"
                      data-testid={`kasbon-attachment-${r.id}`}
                    >
                      <Paperclip className="h-3 w-3" /> lihat
                    </a>
                  ) : <span className="text-stone-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-3 bg-stone-50 text-[11px] text-stone-600">
        <Wallet className="h-3 w-3 inline text-stone-500" /> Akumulasi <b>Pinjam</b> dan <b>Bayar</b> di atas merupakan total untuk bulan {data.month_label}, akan ter-reset otomatis setiap tanggal 1. Sisa Hutang adalah <b>akumulasi seluruh transaksi</b> sejak awal.
      </Card>
    </div>
  );
}
